// The camera on screen: the video and its backdrop, the camera-state overlay and tap zone, and the
// panels for a camera that is waiting for a tap, paused, blocked or unavailable.

import { useEffect } from "react";

const CAM_GLYPH = (
  <svg width="36" height="36" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" aria-hidden="true"><path d="M4 8h3l2-3h6l2 3h3v11H4z" /><circle cx="12" cy="13" r="3.2" /></svg>
);
const CAM_GLYPH_OFF = (
  <svg width="36" height="36" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" aria-hidden="true"><path d="M4 8h3l2-3h6l2 3h3v11H4z" /><circle cx="12" cy="13" r="3.2" /><path d="M3 3l18 18" /></svg>
);
// IOS DRAWS A PLAY BUTTON ON ANY PAUSED INLINE VIDEO, and current Safari ignores the
// ::-webkit-media-controls rules that used to hide it. So a video that is not playing is
// all but transparent (not hidden: Safari will not autoplay a video it thinks is unseen),
// and comes back on its first `playing`. Set on the element, so no render is needed.
const PAUSED = "cap-vid-paused";
function usePausedClass(ref) {
  useEffect(() => {
    const v = ref.current;
    if (!v) return undefined;
    const sync = () => v.classList.toggle(PAUSED, v.paused || v.ended || !v.srcObject);
    const evs = ["playing", "play", "pause", "emptied", "ended", "loadstart", "loadedmetadata"];
    sync();
    evs.forEach((e) => v.addEventListener(e, sync));
    return () => evs.forEach((e) => v.removeEventListener(e, sync));
  }, [ref]);
}

export function CameraStage({ backdropRef, cam, camLiveUi, checking, deciding, live, phase, scanUi, snapMirror, snapMode, snapRef, tapCamera, videoRef }) {
  usePausedClass(videoRef);
  usePausedClass(backdropRef);
  return (
    <>
      {/* The photo step keeps the saved area exact, so the picture is fitted,
      not cropped; the rest of the screen is the same camera, blurred. */}
<video ref={backdropRef} className="cap-backdrop" aria-hidden="true" playsInline muted autoPlay />
<div className="cap-viewport">
        {scanUi ? <div className={"cap-ph" + (camLiveUi ? " cap-ph--off" : "")} aria-hidden="true" /> : null}
        <video
          ref={videoRef}
          className={"cap-video" + (scanUi && !camLiveUi ? " cap-video--wait" : "") + (cam.facing === "user" ? " cap-video--mirror" : "")}
          playsInline
          muted
          autoPlay
        />
        <canvas ref={snapRef} className={"cap-snapshot" + (snapMode ? " cap-snapshot--on" : "") + (snapMirror ? " cap-snapshot--mirror" : "")} aria-hidden="true" />
      </div>
{/* Everything inside this outline is saved; nothing outside it is.
      Hidden while scanning, when nothing is saved and the reticle leads. */}
{live && phase !== "scanning" && !deciding && !checking ? (
        <div className="cap-framearea" aria-hidden="true">
          <i />
          <i />
        </div>
      ) : null}
{phase === "locked" && !scanUi && !cam.live && (cam.tapToPlay || cam.needsTap) ? (
        <button type="button" className="cap-tapzone cap-tapzone--solid" onClick={tapCamera}>
          <span className="cap-cmsg">
            {CAM_GLYPH}
            Tap to turn on the camera
          </span>
        </button>
      ) : null}
    </>
  );
}

export function CameraOverlay({ allowTip, camAsks, camHelp, camState, cancelTyping, hideAllowTip, reticleRef, scanUi, tapCamera, typing }) {
  return (
    <>
      {scanUi && camState !== "blocked" && camState !== "none" ? (
        <div className={"cap-reticle" + (camState === "opening" || camState === "permission" || camState === "resuming" ? " cap-reticle--wait" : camState ? " cap-reticle--still" : "")} ref={reticleRef} aria-hidden="true"><i /><i /><i /><i /><b /></div>
      ) : null}
{scanUi && (camState === "opening" || camState === "permission" || camState === "needs_tap") ? (
        <button type="button" className="cap-tapzone" onClick={tapCamera} aria-label="Turn on the camera" tabIndex={camState === "needs_tap" ? 0 : -1} />
      ) : null}
{scanUi && typing ? <button type="button" className="cap-typeaway" tabIndex={-1} aria-hidden="true" onClick={cancelTyping} /> : null}
{scanUi && camState ? (
        <div className="cap-cammsg">
          {camState === "opening" ? <span className="cap-cpill">Opening camera&hellip;</span> : null}
          {camState === "permission" ? <span className="cap-cpill">Allow the camera to scan</span> : null}
          {camState === "resuming" ? <span className="cap-cpill">Resuming&hellip;</span> : null}
          {camState === "needs_tap" ? <span className="cap-cmsg">{CAM_GLYPH}Tap to turn on the camera</span> : null}
          {camState === "blocked" ? (
            <span className="cap-cmsg">
              {CAM_GLYPH_OFF}
              Camera is blocked
              <small>{camHelp}</small>
            </span>
          ) : null}
        </div>
      ) : null}
{scanUi && !typing && !camState ? <div className="cap-spill"><span>Hold steady &middot; it scans by itself</span></div> : null}
{allowTip ? (
        <button type="button" className="cap-allowtip" onClick={hideAllowTip} aria-label="Hide tip">
          <span className="cap-allowtip-txt">
            <strong>Camera asked {camAsks} times in a row</strong>
            <span><kbd>aA</kbd> &rsaquo; Website Settings &rsaquo; Camera &rsaquo; Allow</span>
          </span>
          <b aria-hidden="true">&times;</b>
        </button>
      ) : null}
    </>
  );
}

export function CameraPanels({ barcode, cam, camGone, camHelp, ident, phase, preScan, presetTitle, resumeCamera, shots, startCamera }) {
  return (
    <>
      {phase === "idle" && !preScan ? (
        <div className="cap-panel">
          <h2>Add photos</h2>
          <p>
            Photograph <b>{presetTitle}</b>.
          </p>
          <button type="button" className="cap-btn" onClick={startCamera}>
            Start camera
          </button>
        </div>
      ) : null}
{phase === "interrupted" ? (
        <div className="cap-panel">
          <h2>Camera paused</h2>
          <p>
            Android stops the camera when this page goes to the background or
            the screen locks.
            {barcode || ident ? (
              <>
                {" "}
                Your item and{" "}
                {shots.length === 1 ? "1 photo are" : `${shots.length} photos are`}{" "}
                still here.
              </>
            ) : null}
          </p>
          <button type="button" className="cap-btn" onClick={resumeCamera}>
            Resume camera
          </button>
        </div>
      ) : null}
{phase === "denied" && !camGone ? (
        <div className="cap-panel">
          {/* The scan step's blocked message, word for word: one look for one problem. */}
          <span className="cap-cmsg">
            {CAM_GLYPH_OFF}
            Camera is blocked
            <small>{camHelp}</small>
          </span>
          <button type="button" className="cap-btn cap-btn--ghost" onClick={startCamera}>
            Try again
          </button>
        </div>
      ) : null}
{phase === "error" && !camGone ? (
        <div className="cap-panel">
          <h2>Camera unavailable</h2>
          <p>{cam.error?.message ?? ""}</p>
          <p>
            A camera is only offered to pages served over <code>https</code> or
            from <code>localhost</code>.
          </p>
          <button type="button" className="cap-btn cap-btn--ghost" onClick={startCamera}>
            Try again
          </button>
        </div>
      ) : null}
    </>
  );
}
